/**
 * @fileoverview Sponsored-tier wire → atom mapping (WEB-36).
 *
 * The backend wire contract (`SponsoredEventMeta.tier`, `@loci/types`) uses
 * the uppercase Prisma `SponsoredTier` enum values (`STARTER | GROWTH |
 * FEATURED_PLUS`). The `@loci/ui` `<SponsoredBadge>` / `<SponsoredTooltip>`
 * atoms take a lowercase `SponsoredTier` prop (`starter | growth | featured`)
 * — NOT a case transform of the wire value (`FEATURED_PLUS.toLowerCase()`
 * would produce `'featured_plus'`, not a valid atom tier). This is the single
 * mapping so every card consumer (Discover / category, Happening Now, and
 * future Map/home-feed surfaces) stays in sync.
 *
 * All three tiers are mapped. WEB-36 shipped the Starter-scoped pill; WEB-537
 * built the Growth/Featured+ card escalation (elevated accent border, Growth
 * "Sponsored by" footer logo, Featured+ "Presented by {advertiser}" header)
 * that this same lowercase tier drives in `DiscoverEventCard`. The Featured+
 * landing hero slot remains a separate surface (WEB-538).
 *
 * @since WEB-36 (Featured / Sponsored Listings — M1 Revenue v0)
 */
import type { SponsoredTier } from '@loci/ui';
import type { SponsoredWireTier } from '@loci/types';

const WIRE_TIER_TO_ATOM_TIER: Record<SponsoredWireTier, SponsoredTier> = {
  STARTER: 'starter',
  GROWTH: 'growth',
  FEATURED_PLUS: 'featured',
};

/** Map the wire-format sponsored tier to the `@loci/ui` atom's tier prop. */
export function mapSponsoredWireTier(tier: SponsoredWireTier): SponsoredTier {
  return WIRE_TIER_TO_ATOM_TIER[tier];
}
